import { useState, useEffect, useCallback } from "react"
import Sidebar from "../features/sidebar/components/sidebar"
import { Column, DataColumn, Table } from "../components/table"
import Papa from "papaparse"
import { saveAs } from "file-saver"
import {
  useGetCompositeScoresByOrgIdQuery,
  useGetGroupsByOrganizationIdQuery,
  useGetCompositeScoresWithBucketQuery,
  usePostFilterUsersByGroupMutation,
  usePostOutputScoresMutation,
} from "../app/services/meseeknaApi"
import { RootState, useTypedDispatch, useTypedSelector } from "../app/store"
import React from "react"
import { AnnotationOptions } from "chartjs-plugin-annotation"
import {
  ChartConfiguration,
  ActiveElement,
  Color,
  ScriptableContext,
  ScriptableAndArray,
} from "chart.js"
import { useLocation, useNavigate } from "react-router-dom"
import ManageModels from "./manage-models"
import {
  setCompositeId,
  updateBenchmark,
  updateComposite,
} from "../features/recruiter/recruiterSlice"
import { skipToken } from "@reduxjs/toolkit/query"
import { RecruiterPortal } from "../utils/constants"
import { useMenuItems } from "../features/sidebar/hooks/useMenuItems"
import { filteredUsersType } from "../features/simulation/types"
function RecruiterDashboard() {
  const location = useLocation()
  const { isOpen } = useTypedSelector((state) => state.sidebar)
  const { composite, benchmark } = useTypedSelector((state) => state.recruiter)
  const menuItems = useMenuItems()

  const dispatch = useTypedDispatch()

  type CompositeScoresWithBucketType = {
    name: string
    color: string
    bucket: number[]
    set_name: string
    tiers_id: number
    measure_id: number
    measure_name: string
    output_score: number
  }

  type measureCodeType = {
    B1: number
    B2: number
    B3: number
    B4: number
    B5: number
    B6: number
    B7: number
    B8: number
    G1: number
    G2: number
    G3: number
    G4: number
    G5: number
    G6: number
  }
  type newBucketsDataType = {
    min: number
    count: number
    renageData: filteredUsersType[]
  }
  type bucketsDataType = {
    measure_id: number
    measure_name: string
    measure_code: keyof measureCodeType
    min_value: number
    max_value: number
    description: string
    color: string
  }

  type ChartState = {
    data: ChartConfiguration<"bar">["data"]
    options: ChartConfiguration<"bar">["options"]
  }

  const authStore = useTypedSelector((state: RootState) => {
    return state.authSlice
  })

  const orgId = authStore.organization?.id
  const navigate = useNavigate()

  //  fetching data
  const { data: groupsListResponse } = useGetGroupsByOrganizationIdQuery(
    orgId ? { organizationId: Number(orgId) } : skipToken,
  )
  const {
    data: compositeScoresData,
    isFetching: isFetchingCompositeScoresData,
  } = useGetCompositeScoresByOrgIdQuery(
    orgId ? { orgId: Number(orgId) } : skipToken,
  )

  const [
    postOutputScoresMutation,
    { isLoading: isPostOutputScoresMutationLoading },
  ] = usePostOutputScoresMutation()
  const [
    postFilterUsersByGroupMutation,
    { isLoading: isPostFilterUsersByGroupMutationLoading },
  ] = usePostFilterUsersByGroupMutation()

  const [filterConfig, setFilterConfig] = useState({})
  const [modelAndButtonState, setModelAndButtonState] = useState<{
    buttonSelection: string | null
    isModelOpen: boolean
  }>({ buttonSelection: null, isModelOpen: false })

  const [tableContentToBeDisplayed, setTableContentToBeDisplayed] = useState<
    {
      check: boolean
      index: number
      usersDetails: DataColumn[]
      usersDetailsRaw?: filteredUsersType
    }[]
  >([])
  const [setAndMeasureIds, setSetAndMeasureIds] = useState<{
    compositeSetId: string | null
    selectedMeasureId: string | null
  }>({ compositeSetId: null, selectedMeasureId: "null initial" })
  const shouldFetchCompositeScoresWithBucket =
    !isNaN(Number(setAndMeasureIds.compositeSetId)) &&
    !isNaN(Number(setAndMeasureIds.selectedMeasureId))

  const {
    data: compositeScoresWithBucketData,
    isFetching: isFetchingCompositeScoresWithBucketData,
  } = useGetCompositeScoresWithBucketQuery(
    shouldFetchCompositeScoresWithBucket
      ? {
          compositeSetId: Number(setAndMeasureIds.compositeSetId),
          measureId: Number(setAndMeasureIds.selectedMeasureId),
        }
      : skipToken,
  )
  const [measureData, setMeasureData] = useState<{
    measuresMenu: { value: string; label: string }[]
    measureTabList: bucketsDataType[] | null
    compositeScoresMenu: { value: string; label: string }[]
  }>({
    measuresMenu: [],
    measureTabList: null,
    compositeScoresMenu: [],
  })
  // Initialising the state with an object containing all chart configurations
  const [chartState, setChartState] = useState<{
    barCompositeChartData: ChartState
    barPerformanceChartData: ChartState
    barBenchmarkChartData: ChartState
    newBuckets: {
      min: number
      count: number
      renageData: filteredUsersType[]
    }[]
    benchmarkNewBuckets: {
      min: number
      count: number
      renageData: filteredUsersType[]
    }[]
    initialTableData: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
      usersDetailsRaw?: filteredUsersType
    }[]
    initialbenchMarkData: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
      usersDetailsRaw?: filteredUsersType
    }[]
    minMaxScore: {
      min: number
      max: number
    }
    bucketTiersData: CompositeScoresWithBucketType[]
  }>({
    barCompositeChartData: {
      data: { labels: [], datasets: [] },
      options: { responsive: true, maintainAspectRatio: false }, //maintainAspectRatio = false allows the chart to be responsive
    },
    barPerformanceChartData: {
      data: { labels: [], datasets: [] },
      options: { responsive: true, maintainAspectRatio: false },
    },
    barBenchmarkChartData: {
      data: { labels: [], datasets: [] },
      options: { responsive: true, maintainAspectRatio: false },
    },
    newBuckets: [],
    benchmarkNewBuckets: [],
    initialTableData: [],
    initialbenchMarkData: [],
    minMaxScore: {
      min: 0,
      max: 0,
    },
    bucketTiersData: [],
  })

  const [graphData, setGraphData] = useState<
    {
      id: number
      graph: string
      data: ChartConfiguration<"bar">["data"]
      options: ChartConfiguration<"bar">["options"]
    }[]
  >([
    {
      id: 0,
      graph: RecruiterPortal.Composite,
      data: chartState.barCompositeChartData.data,
      options: chartState.barCompositeChartData.options,
    },

    {
      id: 1,
      graph: RecruiterPortal.Performance,
      data: chartState.barPerformanceChartData.data,
      options: chartState.barPerformanceChartData.options,
    },
    {
      id: 2,
      graph: RecruiterPortal.OverallPerformance,
      data: chartState.barPerformanceChartData.data,
      options: chartState.barPerformanceChartData.options,
    },
    {
      id: 3,
      graph: RecruiterPortal.Benchmark,
      data: chartState.barBenchmarkChartData.data,
      options: chartState.barBenchmarkChartData.options,
    },
  ])

  const [filteredUsers, setFilteredUsers] = useState<filteredUsersType[]>([])
  const [columnsStore, setColumnsStore] = useState<{
    displayedColumns: Column[]
    initialColumnNames: { compositeColumn: Column[]; benchmarkColumn: Column[] }
  }>({
    displayedColumns: [],
    initialColumnNames: { compositeColumn: [], benchmarkColumn: [] },
  })
  const [groupIds, setGroupIds] = useState<number[]>([])
  const [graphPlaceHolder, setGraphPlaceHolder] = useState<{
    compositeSetEnabled: boolean
    selectMeasureMessage: boolean
  }>({
    compositeSetEnabled: true,
    selectMeasureMessage: false,
  })

  useEffect(() => {
    const tempGroupNames: string[] = []
    groupsListResponse?.data?.forEach((item) => {
      tempGroupNames.push(item.name)
    })
    const filterConfigList = {
      groupName: { values: tempGroupNames },
    }
    setFilterConfig(filterConfigList)
  }, [groupsListResponse])

  const [activeStates, setActiveStates] = useState({
    activeTabName: RecruiterPortal.Composite,
    activeGraph: RecruiterPortal.Composite,
    previousTab: RecruiterPortal.Composite,
  })
  // Extract the tab from the URL path
  const pathSegments = location.pathname.split("/")
  const tabFromPath = pathSegments[pathSegments.length - 1] || "composite"

  const breadcrumbFunctionality = () => {
    switch (activeStates.activeTabName) {
      case RecruiterPortal.Composite:
        setTableContentToBeDisplayed(chartState.initialTableData)
        updateGraphData(
          RecruiterPortal.Composite,
          chartState.barCompositeChartData.data,
          chartState.barCompositeChartData.options,
        )
        break
      case RecruiterPortal.Benchmark:
        setTableContentToBeDisplayed(chartState.initialbenchMarkData)
        updateGraphData(
          RecruiterPortal.Benchmark,
          chartState.barBenchmarkChartData.data,
          chartState.barBenchmarkChartData.options,
        )
        break
    }
    setActiveStates((prevState) => ({
      ...prevState,
      activeGraph:
        activeStates.activeTabName === RecruiterPortal.Composite
          ? RecruiterPortal.Composite
          : RecruiterPortal.Benchmark,
    }))
    setBreadcrumbsDisplay((prevState) =>
      prevState.map((each) => {
        return each.key == 2 ? { ...each, display: false } : each
      }),
    )
  }
  useEffect(() => {
    setBreadcrumbsDisplay((prevState) =>
      prevState.map((each) => {
        return each.key == 1
          ? {
              ...each,
              action: breadcrumbFunctionality,
              label: activeStates.activeTabName,
            }
          : each
      }),
    )
  }, [chartState, activeStates.activeTabName])

  const [breadcrumbsDisplay, setBreadcrumbsDisplay] = useState<
    { label: string; action: () => void; display: boolean; key: number }[]
  >([
    {
      label: authStore.organization.organizationName ?? "",
      action: () => {},
      display: false,
      key: 0,
    },
    {
      label: activeStates.activeTabName,
      action: breadcrumbFunctionality,
      display: true,
      key: 1,
    },
    {
      label: `[]`,
      action: () => {},
      display: false,
      key: 2,
    },
  ])

  useEffect(() => {
    if (tabFromPath && tabFromPath !== activeStates.activeTabName) {
      setActiveStates((prevState) => ({
        ...prevState,
        activeTabName: tabFromPath as RecruiterPortal,
      }))
    }
  }, [tabFromPath, location.pathname, menuItems])

  useEffect(() => {
    const compositeScoresDataFetched = () => {
      let tempCompositeSetId: number | undefined = undefined

      if (
        Array.isArray(compositeScoresData?.data) &&
        compositeScoresData?.data &&
        compositeScoresData?.data?.length > 0
      ) {
        setGraphPlaceHolder({
          compositeSetEnabled: true,
          selectMeasureMessage: false,
        })
      } else {
        setGraphPlaceHolder({
          compositeSetEnabled: false,
          selectMeasureMessage: false,
        })
      }
      if (compositeScoresData?.data?.length) {
        for (let i = compositeScoresData.data.length - 1; i >= 0; i--) {
          const item = compositeScoresData.data[i]
          if (item.buckets_count > 0) {
            tempCompositeSetId = item.id
            break
          }
        }
      }
      setSetAndMeasureIds((prevState) => ({
        ...prevState,
        compositeSetId: tempCompositeSetId?.toString() ?? null,
      }))
      dispatch(setCompositeId(setAndMeasureIds.compositeSetId ?? ""))
      if (!compositeScoresData?.data?.length) {
        setGraphPlaceHolder({
          compositeSetEnabled: false,
          selectMeasureMessage: false,
        })
      }
    }

    if (compositeScoresData) {
      compositeScoresDataFetched()
    }
  }, [compositeScoresData])

  useEffect(() => {
    try {
      const payload = {
        groups: groupIds,
        compositeId: Number(setAndMeasureIds.compositeSetId),
        organizationId: Number(authStore.organization?.id),
        recruiterId: authStore.user?.id,
      }
      let storeResponse: filteredUsersType[] = filteredUsers
      const tempCompositeScoresMenu: { value: string; label: string }[] = []
      const callApi = async () => {
        try {
          const response = await postOutputScoresMutation({
            body: payload,
          }).unwrap()
          storeResponse = response.data
        } catch (error) {
          console.error("Error fetching filtered users:", error)
        }
      }

      const subFunction = async () => {
        await callApi()
        let tempMeasureTabList: bucketsDataType[] = []
        const tempColumns: { name: string; value: string; label: string }[] = []
        const benchmarkColumns: {
          name: string
          value: string
          label: string
        }[] = []
        const measuresMenu: { value: string; label: string }[] = []
        compositeScoresData?.data?.forEach((item) => {
          tempCompositeScoresMenu.push({
            value: item.id.toString(),
            label: item.name,
          })
          if (
            item.id == Number(setAndMeasureIds.compositeSetId) &&
            item.buckets_count !== 0
          ) {
            const bucketsMeasures = item.buckets_data
            tempMeasureTabList = bucketsMeasures

            const bucketsData = item.buckets_data
            const commonColumns = [
              { name: "Name", value: "Name", label: "Name" },
              { name: "Email", value: "Email", label: "Email" },
              { name: "groupName", value: "groupName", label: "Group" },
            ]

            tempColumns.push(...commonColumns)
            benchmarkColumns.push(...commonColumns, {
              name: "Raw Score",
              value: "Raw Score",
              label: "Raw Score",
            })

            for (let k = 0; k < bucketsData.length; k++) {
              tempColumns.push({
                name: bucketsData[k].measure_name,
                value: bucketsData[k].measure_name,
                label: bucketsData[k].measure_name,
              })
              measuresMenu.push({
                value: bucketsData[k].measure_id.toString(),
                label: bucketsData[k].measure_name,
              })
            }
            tempColumns.push({ name: "Total", value: "Total", label: "Total" })
            benchmarkColumns.push({
              name: "Composite Score",
              value: "Composite Score",
              label: "Composite Score",
            })
          }
        })

        setColumnsStore((prevColumnsStore) => ({
          ...prevColumnsStore,
          initialColumnNames: {
            compositeColumn: tempColumns,
            benchmarkColumn: benchmarkColumns,
          },
          displayedColumns: tempColumns,
        }))

        setMeasureData((prevMeasureData) => ({
          ...prevMeasureData,
          measuresMenu: measuresMenu,
          measureTabList: tempMeasureTabList,
          compositeScoresMenu: tempCompositeScoresMenu,
        }))
        const sortedData = [...storeResponse].sort(
          (a, b) => a?.compositeScoresTotal - b?.compositeScoresTotal,
        )
        setFilteredUsers(sortedData)
      }

      const mainFunction = async () => {
        if (setAndMeasureIds.compositeSetId) {
          await subFunction()
        }

        if (activeStates.activeTabName === RecruiterPortal.Benchmark) {
          if (activeStates.activeGraph !== RecruiterPortal.Performance) {
            setGraphPlaceHolder({
              compositeSetEnabled: false,
              selectMeasureMessage: true,
            })
            setColumnsStore((prevColumnsStore) => ({
              ...prevColumnsStore,
              displayedColumns:
                prevColumnsStore.initialColumnNames.benchmarkColumn,
            }))
          }
        }
      }
      mainFunction()
    } catch (e) {
      console.log("check the err", e)
    }
  }, [setAndMeasureIds.compositeSetId])

  useEffect(() => {
    try {
      const payload = {
        groups: groupIds,
        compositeId: Number(setAndMeasureIds.compositeSetId),
        organizationId: Number(authStore.organization?.id),
        recruiterId: authStore.user?.id,
      }
      let storeResponse: filteredUsersType[] = filteredUsers
      const callByGroupApi = async () => {
        try {
          const response = await postFilterUsersByGroupMutation({
            body: payload,
          })?.unwrap()
          storeResponse = [...response.data].sort(
            (a, b) => a?.compositeScoresTotal - b?.compositeScoresTotal,
          )
        } catch (error) {
          console.error("Error fetching filtered users:", error)
        }
      }

      const groupBasedDisplayPrep = async () => {
        await callByGroupApi()
        if (activeStates.activeTabName === RecruiterPortal.Composite) {
          const {
            barChartDataInitialVariable,
            barChartOptionsInitialVariable,
          } = initialGraphPrep(storeResponse)

          setGraphData((pre) =>
            pre.map((item) => {
              if (item.graph === RecruiterPortal.Composite) {
                return {
                  ...item,
                  data: barChartDataInitialVariable,
                  options: barChartOptionsInitialVariable,
                }
              }
              return item
            }),
          )
        } else if (activeStates.activeTabName === RecruiterPortal.Performance) {
          const { performanceChartData, performaceChartOptions } =
            performanceChartDataPrep(storeResponse)

          setGraphData((pre) =>
            pre.map((item) => {
              if (item.graph === RecruiterPortal.OverallPerformance) {
                return {
                  ...item,
                  data: performanceChartData,
                  options: performaceChartOptions,
                }
              }
              return item
            }),
          )
        } else if (activeStates.activeTabName === RecruiterPortal.Benchmark) {
          const { barBenchmarkChartData, barBenchmarkChartOptions } =
            benchmarkPrep(storeResponse, chartState.bucketTiersData)

          setGraphData((pre) =>
            pre.map((item) => {
              if (item.graph === RecruiterPortal.Benchmark) {
                return {
                  ...item,
                  data: barBenchmarkChartData,
                  options: barBenchmarkChartOptions,
                }
              }
              return item
            }),
          )
        }
        //handled integrated graph for benchmark and composite
        if (
          breadcrumbsDisplay[2].display == true &&
          (activeStates.activeTabName === RecruiterPortal.Benchmark ||
            activeStates.activeTabName === RecruiterPortal.Composite)
        ) {
          const { performanceChartData, performaceChartOptions } =
            performanceChartDataPrep(storeResponse)
          updateGraphData(
            RecruiterPortal.Performance,
            performanceChartData,
            performaceChartOptions,
          )
        }

        const { formattedUsersData } = convertToTableFormat(
          storeResponse,
          activeStates.activeTabName,
        )
        setTableContentToBeDisplayed(formattedUsersData)
      }
      if (groupIds.length !== 0) {
        groupBasedDisplayPrep()
      }
    } catch (e) {
      console.log("error in groupIds", e)
    }
  }, [groupIds])

  useEffect(() => {
    if (groupIds.length > 0) {
      setGroupIds([])
      setGraphData((previous) =>
        previous.map((item) => {
          if (
            item.graph === RecruiterPortal.Performance ||
            item.graph === RecruiterPortal.OverallPerformance
          ) {
            return {
              ...item,
              data: chartState.barPerformanceChartData.data,
              options: chartState.barPerformanceChartData.options,
            }
          } else if (item.graph === RecruiterPortal.Composite) {
            return {
              ...item,
              data: chartState.barCompositeChartData.data,
              options: chartState.barCompositeChartData.options,
            }
          } else {
            return item
          }
        }),
      )
    }

    if (
      activeStates.activeTabName === RecruiterPortal.Performance ||
      activeStates.activeTabName === RecruiterPortal.Composite
    ) {
      setColumnsStore((previous) => ({
        ...previous,
        displayedColumns: columnsStore.initialColumnNames.compositeColumn,
      }))

      setTableContentToBeDisplayed(chartState.initialTableData)

      if (activeStates.activeTabName === RecruiterPortal.Performance) {
        setActiveStates((previous) => ({
          ...previous,
          previousTab: RecruiterPortal.Performance,
          activeGraph: RecruiterPortal.OverallPerformance,
        }))
      }

      if (activeStates.activeTabName === RecruiterPortal.Composite) {
        setActiveStates((previous) => ({
          ...previous,
          previousTab: RecruiterPortal.Composite,
          activeGraph: RecruiterPortal.Composite,
        }))
        setModelAndButtonState((previous) => ({
          ...previous,
          buttonSelection: RecruiterPortal.Composite,
        }))
      }
    }

    if (activeStates.activeTabName === RecruiterPortal.Benchmark) {
      setModelAndButtonState((previous) => ({
        ...previous,
        buttonSelection: "measures",
        isModelOpen: true,
      }))
    }

    setBreadcrumbsDisplay((prevState) => {
      if (activeStates.activeTabName === RecruiterPortal.Performance) {
        return prevState?.map((item, index) => {
          if (index === 1) {
            return {
              ...item,
              label: activeStates.activeTabName,
            }
          }
          if (
            activeStates.activeTabName === RecruiterPortal.Performance &&
            index === 2
          ) {
            return {
              ...item,
              label: `[${chartState.minMaxScore.min},${chartState.minMaxScore.max}]`,
              display: true,
            }
          }
          return item
        })
      } else {
        return prevState?.map((item, index) =>
          index === 1
            ? { ...item, label: activeStates.activeTabName }
            : index === 2
              ? { ...item, display: false }
              : item,
        )
      }
    })
  }, [activeStates.activeTabName])

  const benchmarkPrep = (
    usersData: filteredUsersType[],
    bucketTiersData: CompositeScoresWithBucketType[],
  ) => {
    let barBenchmarkChartData: ChartConfiguration<"bar">["data"] = {
      labels: [],
      datasets: [],
    }
    let barBenchmarkChartOptions: ChartConfiguration<"bar">["options"] = {
      responsive: true,
      maintainAspectRatio: false,
    }
    const tempBucketsByMeasure: {
      min: number
      count: number
      renageData: filteredUsersType[]
    }[] = []

    try {
      const selectedMeasure = measureData.measureTabList?.find(
        (item) =>
          item.measure_id === Number(setAndMeasureIds.selectedMeasureId),
      )
      const measureMinValue = selectedMeasure?.min_value
      const measureMaxValue = selectedMeasure?.max_value
      const selectedMeasureCode: keyof filteredUsersType =
        selectedMeasure?.measure_code as keyof filteredUsersType
      const selectedMeasureName = selectedMeasure?.measure_name
      const buckets = bucketTiersData.map((item) => item.bucket).flat()
      const minMeasure = Math.min(...buckets)
      const maxMeasure = Math.max(...buckets)

      const userDataMap = new Map<number, filteredUsersType[]>()

      usersData.forEach((user) => {
        const score = user[selectedMeasureCode]
        if (!userDataMap.has(Number(score))) {
          userDataMap.set(Number(score), [])
        }
        userDataMap.get(Number(score))?.push(user)
      })

      const userLabelArray: string[] = []
      const displayGraphArray: number[] = []
      for (
        let i = Number(measureMinValue);
        i <= Number(measureMaxValue);
        i += 1
      ) {
        userLabelArray.push(i.toString())
        const usersWithScore = userDataMap.get(i) || []
        displayGraphArray.push(usersWithScore.length)
        tempBucketsByMeasure.push({
          min: i,
          count: usersWithScore.length,
          renageData: usersWithScore,
        })
      }

      const maxYs = Math.ceil((Math.max(...displayGraphArray) + 1) / 2) * 2
      const benchmarkAnnotations: AnnotationOptions<"box">[] =
        bucketTiersData.map((tier) => ({
          type: "box",
          drawTime: "beforeDraw",
          xScaleID: "x",
          yScaleID: "y",
          xMin: tier.bucket[0] - 0.5,
          xMax: tier.bucket[1] + 0.5,
          yMin: 0,
          yMax: maxYs + 30,
          backgroundColor: `${tier.color}`,
          borderColor: `${tier.color}`,
          borderWidth: 0,
          borderRadius: 0,
        }))

      barBenchmarkChartData = {
        labels: userLabelArray,
        datasets: [
          {
            data: displayGraphArray,
          },
        ],
      }

      barBenchmarkChartOptions = {
        maintainAspectRatio: false,
        responsive: true,

        interaction: {
          mode: "index",
        },

        scales: {
          x: {
            suggestedMin: -0.5,
            min: minMeasure,
            max: maxMeasure,
            offset: true,
            grid: {
              display: false,
            },
          },
          y: {
            type: "linear",
            max: maxYs + 30,
          },
        },

        onHover: function (event, chartElement) {
          const target: HTMLElement = event.native?.target as HTMLElement
          if (chartElement.length > 0) {
            target.style.cursor = "pointer"
          } else {
            target.style.cursor = "default"
          }
        },
        onClick: (event, elements) => {
          if (elements.length > 0) {
            onClickOfBenchmarkBar(elements, tempBucketsByMeasure)
          }
        },
        plugins: {
          title: {
            display: true,
            text: selectedMeasureName,
            font: { size: 20 },
            padding: { bottom: 10 },
          },
          tooltip: {},
          annotation: {
            annotations: benchmarkAnnotations,
          },
          legend: {
            display: false,
          },
        },
      }

      return {
        barBenchmarkChartData,
        barBenchmarkChartOptions,
        tempBucketsByMeasure,
      }
    } catch (e) {
      console.log("error", e)
    }

    return {
      barBenchmarkChartData,
      barBenchmarkChartOptions,
      tempBucketsByMeasure,
    }
  }
  /*here we are the graph data after every tab change and also when the users data is updated
other wise the table data is updated when the bars are clicked
performance and compo chart
*/
  useEffect(() => {
    const {
      barChartDataInitialVariable,
      barChartOptionsInitialVariable,
      newBucketsData,
    } = initialGraphPrep(filteredUsers)
    const { performanceChartData, performaceChartOptions } =
      performanceChartDataPrep(filteredUsers)
    const benchmarkFormattedData = convertToTableFormat(
      filteredUsers,
      RecruiterPortal.Benchmark,
    ).formattedUsersData
    const compositeFormattedData = convertToTableFormat(
      filteredUsers,
      RecruiterPortal.Composite,
    ).formattedUsersData
    const formattedUsersData =
      activeStates.activeTabName == RecruiterPortal.Benchmark
        ? benchmarkFormattedData
        : compositeFormattedData
    setTableContentToBeDisplayed(formattedUsersData)
    const minScore = filteredUsers[0]?.compositeScoresTotal
    const maxScore =
      filteredUsers[filteredUsers.length - 1]?.compositeScoresTotal

    //graphs updating
    setChartState((prevState) => ({
      ...prevState,
      barCompositeChartData: {
        data: barChartDataInitialVariable,
        options: barChartOptionsInitialVariable,
      },
      barPerformanceChartData: {
        data: performanceChartData,
        options: performaceChartOptions,
      },
      newBuckets: newBucketsData,
      initialTableData: compositeFormattedData,
      initialbenchMarkData: formattedUsersData,
      minMaxScore: {
        min: minScore,
        max: maxScore,
      },
    }))

    setBreadcrumbsDisplay((prevState) =>
      prevState.map((item, index) => {
        if (index == 2) {
          return {
            ...item,
            label: `[${minScore},${maxScore}]`,
            display: false,
          }
        }
        return item
      }),
    )

    setGraphData((pre) =>
      pre.map((item) => {
        if (item.graph === RecruiterPortal.Composite) {
          return {
            ...item,
            data: barChartDataInitialVariable,
            options: barChartOptionsInitialVariable,
          }
        }
        if (
          item.graph === RecruiterPortal.Performance ||
          item.graph === RecruiterPortal.OverallPerformance
        ) {
          return {
            ...item,
            data: performanceChartData,
            options: performaceChartOptions,
          }
        }

        return item
      }),
    )
  }, [filteredUsers, measureData.measureTabList])

  const onClickOfCompositeBar = (
    elements: ActiveElement[],
    newBucketsData: newBucketsDataType[],
  ) => {
    const indexSelected = elements[0].index
    const data = newBucketsData[indexSelected]
    commonFunction(data, RecruiterPortal.Composite)
    dispatch(
      updateComposite({
        breadCrumbId: data?.min,
        data: data?.renageData,
        compositeId: setAndMeasureIds.compositeSetId ?? "",
      }),
    )
  }

  const commonFunction = (data: newBucketsDataType, activeTabName: string) => {
    const { performanceChartData, performaceChartOptions } =
      performanceChartDataPrep(data?.renageData)
    const { formattedUsersData } = convertToTableFormat(
      data?.renageData,
      activeTabName,
    )
    setTableContentToBeDisplayed(formattedUsersData)
    setActiveStates((prevState) => ({
      ...prevState,
      activeGraph: RecruiterPortal.Performance,
    }))
    setGraphData((pre) =>
      pre.map((item) => {
        if (item.graph === RecruiterPortal.Performance) {
          return {
            ...item,
            data: performanceChartData,
            options: performaceChartOptions,
          }
        }
        return item
      }),
    )
    setBreadcrumbsDisplay((prevState) =>
      prevState.map((item, index) => {
        if (index == 2) {
          return {
            ...item,
            label: `[${data?.min}]`,
            display: true,
          }
        }
        return item
      }),
    )
  }

  const onClickOfBenchmarkBar = (
    elements: ActiveElement[],
    newBucketsData: newBucketsDataType[],
  ) => {
    const indexSelected = elements[0].index
    const data: newBucketsDataType = newBucketsData[indexSelected]

    commonFunction(data, RecruiterPortal.Benchmark)
    dispatch(
      updateBenchmark({
        breadCrumbId: data?.min,
        data: data?.renageData,
        measureId: setAndMeasureIds.selectedMeasureId ?? "",
        compositeId: setAndMeasureIds.compositeSetId ?? "",
      }),
    )
  }

  function initialGraphPrep(storeResponse: filteredUsersType[]): {
    barChartDataInitialVariable: ChartConfiguration<"bar">["data"]
    barChartOptionsInitialVariable: ChartConfiguration<"bar">["options"]
    newBucketsData: {
      min: number
      count: number
      renageData: filteredUsersType[]
    }[]
  } {
    try {
      const minScore = storeResponse[0]?.compositeScoresTotal
      const maxScore =
        storeResponse[storeResponse.length - 1]?.compositeScoresTotal
      const newBucketsData: {
        min: number
        count: number
        renageData: filteredUsersType[]
      }[] = []
      const labelArray = []
      const bucketArray = []
      if (maxScore !== 0) {
        const scoreMap = new Map<number, filteredUsersType[]>()
        storeResponse.forEach((user) => {
          const score = user.compositeScoresTotal
          if (!scoreMap.has(score)) {
            scoreMap.set(score, [])
          }
          scoreMap.get(score)?.push(user)
        })

        for (let i = minScore; i <= maxScore; i += 1) {
          const usersWithScore = scoreMap.get(i) || []
          labelArray.push(i)
          bucketArray.push(usersWithScore.length)
          newBucketsData.push({
            min: i,
            count: usersWithScore.length,
            renageData: usersWithScore,
          })
        }
      }

      const barChartDataInitialVariable: ChartConfiguration<"bar">["data"] = {
        labels: labelArray,
        datasets: [{
          data: bucketArray,
          backgroundColor: '#8B5CF6',  // violet-500 - matches page accent
        }],
      }
      const barChartOptionsInitialVariable: ChartConfiguration<"bar">["options"] =
        {
          scales: {
            x: {
              grid: {
                display: false,
              },
            },
            y: {
              type: "linear",
              ticks: {
                callback: function (value) {
                  return value
                },
              },
            },
          },
          onClick: (event, elements) => {
            if (elements.length > 0) {
              onClickOfCompositeBar(elements, newBucketsData)
            }
          },
          plugins: {
            legend: {
              display: false,
            },
            title: {
              display: true,
              text: "Cognitive Performance Insights",
              font: { size: 20 },
              padding: { bottom: 8 },
            },
          },
          interaction: {
            mode: "index",
          },
          responsive: true,
          maintainAspectRatio: false,
          onHover: function (event, chartElement) {
            const target = event.native?.target
            const element = target as HTMLElement
            if (chartElement.length > 0) {
              element.style.cursor = "pointer"
            } else {
              element.style.cursor = "default"
            }
          },
        }

      return {
        barChartDataInitialVariable,
        barChartOptionsInitialVariable,
        newBucketsData,
      }
    } catch (e) {
      console.error("error in initialGraphPerp", e)
      return {
        barChartDataInitialVariable: { datasets: [] },
        barChartOptionsInitialVariable: {},
        newBucketsData: [],
      }
    }
  }

  function convertToTableFormat(
    storeResponse: filteredUsersType[],
    tabName: string,
  ) {
    try {
      const formattedUsersData: {
        check: boolean
        index: number
        usersDetails: DataColumn[]
        usersDetailsRaw?: filteredUsersType
      }[] = []

      const selectedMeasure = measureData.measureTabList?.find(
        (item) =>
          item.measure_id === Number(setAndMeasureIds.selectedMeasureId),
      )
      const selectedMeasureCode: keyof filteredUsersType =
        selectedMeasure?.measure_code as keyof filteredUsersType
      storeResponse.forEach((user: filteredUsersType, index: number) => {
        const returnValue: DataColumn[] = []
        const userKeys = Object.keys(user)
        const userValues = Object.values(user)
        const allBucketCodes = measureData.measureTabList?.map(
          (item) => item.measure_code,
        ) as (keyof filteredUsersType)[]

        userKeys.forEach((key, i) => {
          const value = userValues[i]
          const id = (index * userKeys.length + (i + 1)).toString()

          if (
            [
              "name",
              "email",
              "groupName",
              "userId",
              "runId",
              "organizationId",
              "cplan_count",
              "connections_count",
              "eplan_count",
              "total_connections",
              "rawScoresTotal",
              "compositeScoresTotal",
              "final_total",
              "raw_total",
            ].includes(key)
          ) {
            if (
              ["name", "email", "groupName", "compositeScoresTotal"].includes(
                key,
              )
            ) {
              const newObj = {
                id,
                name: key === "compositeScoresTotal" ? "Total" : key,
                value: value
                  ? value.toString()
                  : key === "name"
                    ? user.email?.split("@")[0]
                    : "",
              }

              if (key === "name") {
                returnValue.unshift(newObj)
              } else if (key === "email") {
                returnValue.push(newObj)
              } else if (
                !(
                  key === "compositeScoresTotal" &&
                  tabName === RecruiterPortal.Benchmark
                )
              ) {
                returnValue.push(newObj)
              }
            }
          } else {
            if (tabName !== RecruiterPortal.Benchmark) {
              if (!allBucketCodes?.includes(key as keyof filteredUsersType)) {
                const columnName =
                  measureData.measureTabList?.find(
                    (item) => `${item.measure_code}_output_score` === key,
                  )?.measure_name ?? ""

                if (columnName) {
                  returnValue.push({
                    id,
                    name: columnName,
                    value: value ? value.toString() : "0",
                  })
                }
              }
            } else {
              if (key === selectedMeasureCode) {
                returnValue.push(
                  {
                    id,
                    name: "Raw Score",
                    value: value ? value.toString() : "0",
                  },
                  {
                    id,
                    name: "Composite Score",
                    value: userValues[i + 1]
                      ? userValues[i + 1].toString()
                      : "0",
                  },
                )
              }
            }
          }
        })

        formattedUsersData.push({
          check: false,
          index: index,
          usersDetails: returnValue,
          usersDetailsRaw: user,
        })
      })

      return {
        formattedUsersData,
      }
    } catch (error) {
      return {
        formattedUsersData: [],
      }
    }
  }
  const performanceChartDataPrep = (usersData: filteredUsersType[]) => {
    let dataBox: {
      label: string
      backgroundColor: string
      hoverBackgroundColor: ScriptableAndArray<Color, ScriptableContext<"bar">>
      data: number[]
    }[] = []
    const names: string[] = []
    let maxScore: number = 0
    maxScore = usersData[usersData.length - 1]?.compositeScoresTotal
    const dataBoxMap: Record<string, number[]> = {}

    measureData.measureTabList?.forEach(({ measure_code }) => {
      dataBoxMap[measure_code] = []
    })

    usersData.forEach((eachItem) => {
      const name = eachItem.name || eachItem.email.split("@")[0]
      names.push(name)

      const userScores: Record<string, number> = {}

      for (const { measure_code } of measureData.measureTabList || []) {
        const score = eachItem[`${measure_code}_output_score`] || 0
        userScores[measure_code] = score

        dataBoxMap[measure_code].push(score)
      }
    })

    dataBox =
      measureData.measureTabList?.map(
        ({ measure_name, color, measure_code }) => ({
          label: measure_name,
          backgroundColor: color,
          hoverBackgroundColor: (context: ScriptableContext<"bar">) =>
            context.chart.data.datasets[context.datasetIndex]
              ?.backgroundColor as Color,
          data: dataBoxMap[measure_code],
        }),
      ) || []

    const performanceChartDataVariable = {
      labels: names,
      datasets: dataBox,
    }
    const performaceChartOptionsVariable: ChartConfiguration<"bar">["options"] =
      {
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false,
            },
            ticks: {
              display: false,
            },
          },
          y: {
            stacked: true,
            type: "linear",
          },
        },

        plugins: {
          legend: {
            position: "bottom",
          },
          title: {
            display: false,
            text: "Participant Performance Graph",
            font: { size: 18 },
            padding: { bottom: 8 },
          },
          annotation: {
            annotations: {
              line1: {
                type: "line",
                yMin: maxScore,
                yMax: maxScore,
                borderColor: "darkgrey",
                borderWidth: 2,
              },
            },
          },
        },
        interaction: {
          mode: "index",
        },
        responsive: true,
        maintainAspectRatio: false,
      }

    return {
      performanceChartData: performanceChartDataVariable,
      performaceChartOptions: performaceChartOptionsVariable,
    }
  }

  useEffect(() => {
    try {
      let bucketTiersData: CompositeScoresWithBucketType[] = []
      const fetchCompositeScoresWithBucketData = async () => {
        const response = compositeScoresWithBucketData
        if (response?.code === 200 && response?.data?.length > 0) {
          bucketTiersData = response.data
        }
      }

      const benchmarkChartDataPrep = () => {
        fetchCompositeScoresWithBucketData()

        if (bucketTiersData.length > 0) {
          const {
            barBenchmarkChartData,
            barBenchmarkChartOptions,
            tempBucketsByMeasure,
          } = benchmarkPrep(filteredUsers, bucketTiersData)

          const { formattedUsersData } = convertToTableFormat(
            filteredUsers,
            activeStates.activeTabName,
          )
          setColumnsStore((prevState) => ({
            ...prevState,
            displayedColumns: columnsStore.initialColumnNames.benchmarkColumn,
          }))
          setTableContentToBeDisplayed(formattedUsersData)
          setChartState((prevState) => ({
            ...prevState,
            barBenchmarkChartData: {
              data: barBenchmarkChartData,
              options: barBenchmarkChartOptions,
            },
            benchmarkNewBuckets: tempBucketsByMeasure,
            initialbenchMarkData: formattedUsersData,
            bucketTiersData: bucketTiersData,
          }))
          updateGraphData(
            RecruiterPortal.Benchmark,
            barBenchmarkChartData,
            barBenchmarkChartOptions,
          )
          setActiveStates((prevState) => ({
            ...prevState,
            activeGraph: RecruiterPortal.Benchmark,
          }))
        }
      }
      if (setAndMeasureIds.selectedMeasureId !== null) {
        benchmarkChartDataPrep()
      }
    } catch (error) {
      //console.log("error in benchmarkChartDataPrep", error)
    }
  }, [compositeScoresWithBucketData])

  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setIsLoading(
      isFetchingCompositeScoresData ||
        isFetchingCompositeScoresWithBucketData ||
        isPostOutputScoresMutationLoading ||
        isPostFilterUsersByGroupMutationLoading,
    )
  }, [
    isFetchingCompositeScoresData,
    isFetchingCompositeScoresWithBucketData,
    isPostOutputScoresMutationLoading,
    isPostFilterUsersByGroupMutationLoading,
  ])

  const handleCompositeScoresSetSelection = (
    e: React.MouseEvent<HTMLButtonElement>,
    type?: string,
  ) => {
    setModelAndButtonState((prevState) => ({
      ...prevState,
      buttonSelection: type ?? "measures",
      isModelOpen: true,
    }))
  }

  const buttons = React.useMemo(
    () => [
      {
        action: (event: React.MouseEvent<HTMLButtonElement>) =>
          handleCompositeScoresSetSelection(event, "overlay"),
        label: "Overlay",
        disable: !(activeStates.activeTabName === RecruiterPortal.Performance),
        hide: !(activeStates.activeTabName === RecruiterPortal.Performance),
      },
      {
        action: (event: React.MouseEvent<HTMLButtonElement>) =>
          handleCompositeScoresSetSelection(event, "measures"),
        label: "Measures",
        disable: !(activeStates.activeTabName === RecruiterPortal.Benchmark),
        hide: !(activeStates.activeTabName === RecruiterPortal.Benchmark),
      },
      {
        action: (event: React.MouseEvent<HTMLButtonElement>) =>
          handleCompositeScoresSetSelection(event, RecruiterPortal.Composite),
        label:
          activeStates.activeTabName === RecruiterPortal.Benchmark
            ? "Benchmark Score Set"
            : "Composite Scores sets",
        disable: false,
        hide:
          activeStates.activeGraph === RecruiterPortal.Performance &&
          (activeStates.activeTabName == RecruiterPortal.Composite ||
            activeStates.activeTabName == RecruiterPortal.Benchmark),
      },
      {
        action: () => {},
        label: "Export Users",
        disable: false,
        hide: false,
      },
    ],
    [activeStates.activeTabName, activeStates.activeGraph],
  )

  const handleModelToggle = (state: boolean, buttonSelected: string) => {
    if (
      activeStates.activeTabName === RecruiterPortal.Benchmark &&
      buttonSelected.includes("measures")
    ) {
      if (state == false) {
        setModelAndButtonState((prevState) => ({
          ...prevState,
          isModelOpen: state,
        }))
      }
      if (activeStates.previousTab !== RecruiterPortal.Benchmark) {
        const pathToChange = menuItems.find((item) =>
          item.name.toLowerCase().includes(activeStates.previousTab),
        )

        //navigate to previous tab
        if (!state && pathToChange) {
          setActiveStates((prevState) => ({
            ...prevState,
            activeTabName: activeStates.previousTab,
          }))
          setModelAndButtonState((prevState) => ({
            ...prevState,
            isModelOpen: false,
          }))
          navigate(pathToChange.path, {
            state: {
              group: { id: pathToChange.id, name: pathToChange.name },
            },
          })
        }
      }
    } else {
      setModelAndButtonState((prevState) => ({
        ...prevState,
        isModelOpen: state,
      }))
    }
  }
  const handleSubmit = (selectedId: string) => {
    if (modelAndButtonState.buttonSelection === "measures") {
      setGraphPlaceHolder({
        compositeSetEnabled: true,
        selectMeasureMessage: false,
      })
      if (setAndMeasureIds.selectedMeasureId !== selectedId) {
        setSetAndMeasureIds((prevState) => ({
          ...prevState,
          selectedMeasureId: selectedId,
        }))
        setActiveStates((prevState) => ({
          ...prevState,
          previousTab: RecruiterPortal.Benchmark,
        }))
      } else {
        setActiveStates((prevState) => ({
          ...prevState,
          previousTab: RecruiterPortal.Benchmark,
          activeTabName: RecruiterPortal.Benchmark,
          activeGraph: RecruiterPortal.Benchmark,
        }))
        setColumnsStore((prevState) => ({
          ...prevState,
          displayedColumns: prevState.initialColumnNames.benchmarkColumn,
        }))
        updateGraphData(
          RecruiterPortal.Benchmark,
          chartState.barBenchmarkChartData.data,
          chartState.barBenchmarkChartData.options,
        )
        setTableContentToBeDisplayed(chartState.initialbenchMarkData)
        setModelAndButtonState((prevState) => ({
          ...prevState,
          isModelOpen: false,
        }))
      }
    } else if (
      modelAndButtonState.buttonSelection === RecruiterPortal.Composite
    ) {
      if (activeStates.activeTabName === RecruiterPortal.Benchmark) {
        setSetAndMeasureIds((prevState) => ({
          ...prevState,
          selectedMeasureId: null,
        }))
      }
      setSetAndMeasureIds((prevState) => ({
        ...prevState,
        compositeSetId: selectedId,
      }))
      dispatch(setCompositeId(setAndMeasureIds.compositeSetId ?? ""))
    } else if (modelAndButtonState.buttonSelection === "overlay") {
      setGraphData((prevState) => {
        const updatedGraphData = prevState?.map((item) =>
          item.graph === RecruiterPortal.Performance ||
          item.graph === RecruiterPortal.OverallPerformance
            ? {
                ...item,
                options: {
                  ...item.options,
                  plugins: {
                    annotation: {
                      annotations: {
                        line1: {
                          yMin: Number(selectedId),
                          yMax: Number(selectedId),
                        },
                      },
                    },
                  },
                },
              }
            : item,
        )
        return updatedGraphData
      })
    }
    setModelAndButtonState((prevState) => ({
      ...prevState,
      isModelOpen: false,
    }))
  }
  const exportUsers = useCallback(
    (
      currentRows: {
        check: boolean
        index: number
        usersDetails: DataColumn[]
        usersDetailsRaw?: filteredUsersType
      }[],
    ) => {
      let csvData = []
      const customFields = columnsStore.displayedColumns.map(
        (column) => column.label,
      )

      csvData = currentRows.map((row) =>
        customFields.map((field) => {
          const detail = row.usersDetails.find((detail) => {
            const name =
              detail.name == "name"
                ? "Name"
                : detail.name == "email"
                  ? "Email"
                  : detail.name == "groupName"
                    ? "Group"
                    : detail.name
            return name === field
          })
          return detail ? detail.value : ""
        }),
      )

      const csv = Papa.unparse({
        fields: customFields.filter(
          (field): field is string => field !== undefined,
        ),
        data: csvData,
      })

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })

      saveAs(
        blob,
        `${
          (authStore.organization.organizationName
            ? authStore.organization.organizationName
            : ""
          ).replace(/\s+/g, "_") + "Users"
        }.csv`,
      )
    },
    [columnsStore.displayedColumns],
  )

  const updateGraphData = (
    graphName: string,
    data: ChartConfiguration<"bar">["data"],
    options: ChartConfiguration<"bar">["options"],
  ) => {
    setGraphData((prev) =>
      prev.map((item) => {
        return item.graph === graphName
          ? {
              ...item,
              data,
              options,
            }
          : item
      }),
    )
  }

  const updateDataByGroups = (
    groupsSelected: string[],
    state?: string,
    type?: string,
  ) => {
    if (state === "clear") {
      if (
        breadcrumbsDisplay[2].display == true &&
        activeStates.activeTabName !== RecruiterPortal.Performance
      ) {
        const data =
          type === "integrated" &&
          activeStates.activeTabName == RecruiterPortal.Composite
            ? composite?.data
            : benchmark?.data
        const { performanceChartData, performaceChartOptions } =
          performanceChartDataPrep(data)
        const { formattedUsersData } = convertToTableFormat(
          data,
          activeStates.activeTabName,
        )
        setTableContentToBeDisplayed(formattedUsersData)
        updateGraphData(
          RecruiterPortal.Performance,
          performanceChartData,
          performaceChartOptions,
        )
      } else {
        if (activeStates.activeTabName === RecruiterPortal.Benchmark) {
          setTableContentToBeDisplayed(chartState.initialbenchMarkData)
        } else {
          setTableContentToBeDisplayed(chartState.initialTableData)
        }

        switch (activeStates.activeTabName) {
          case RecruiterPortal.Composite:
            updateGraphData(
              RecruiterPortal.Composite,
              chartState.barCompositeChartData.data,
              chartState.barCompositeChartData.options,
            )
            break
          case RecruiterPortal.Benchmark:
            updateGraphData(
              RecruiterPortal.Benchmark,
              chartState.barBenchmarkChartData.data,
              chartState.barBenchmarkChartData.options,
            )
            break
          default:
            updateGraphData(
              RecruiterPortal.OverallPerformance,
              chartState.barPerformanceChartData.data,
              chartState.barPerformanceChartData.options,
            )
        }
      }
      setGroupIds([])
    } else {
      if (
        breadcrumbsDisplay[2].display == true &&
        activeStates.activeTabName !== RecruiterPortal.Performance &&
        type == "integrated"
      ) {
        const data =
          type === "integrated" &&
          activeStates.activeTabName == RecruiterPortal.Composite
            ? composite?.data
            : benchmark?.data
        const filteredDataForIntegrated = data?.filter((each) => {
          const splittedGroups =
            each.groupName == null ? [] : each.groupName.split(",")
          return splittedGroups.some((group) => groupsSelected.includes(group))
        })
        const { performanceChartData, performaceChartOptions } =
          performanceChartDataPrep(filteredDataForIntegrated)
        const { formattedUsersData } = convertToTableFormat(
          filteredDataForIntegrated,
          activeStates.activeTabName,
        )
        setTableContentToBeDisplayed(formattedUsersData)
        updateGraphData(
          RecruiterPortal.Performance,
          performanceChartData,
          performaceChartOptions,
        )
      } else {
        const groups = groupsListResponse?.data?.filter((item) => {
          return groupsSelected?.includes(item.name) ? item.id : null
        })
        if (groups) {
          setGroupIds(groups.map((group) => group.id))
        }
      }
    }
  }
  const handleSearchUpdateWithGraph = (
    data: filteredUsersType[],
    tabShift?: boolean,
  ) => {
    if (tabShift) {
      const { performanceChartData, performaceChartOptions } =
        performanceChartDataPrep(data)
      const { barChartDataInitialVariable, barChartOptionsInitialVariable } =
        initialGraphPrep(data)

      setGraphData((prev) =>
        prev.map((item) => {
          // console.log(item.graph, "item....")
          return item.graph === RecruiterPortal.Performance ||
            item.graph === RecruiterPortal.OverallPerformance
            ? {
                ...item,
                data: performanceChartData,
                options: performaceChartOptions,
              }
            : item.graph === RecruiterPortal.Composite
              ? {
                  ...item,
                  data: barChartDataInitialVariable,
                  options: barChartOptionsInitialVariable,
                }
              : item
        }),
      )
    } else {
      if (
        activeStates.activeTabName == RecruiterPortal.Performance &&
        activeStates.activeGraph == RecruiterPortal.OverallPerformance
      ) {
        const { performanceChartData, performaceChartOptions } =
          performanceChartDataPrep(data)
        // console.log(data,"data....")
        updateGraphData(
          RecruiterPortal.OverallPerformance,
          performanceChartData,
          performaceChartOptions,
        )
      }
      if (
        activeStates.activeTabName == RecruiterPortal.Composite &&
        activeStates.activeGraph == RecruiterPortal.Composite
      ) {
        const { barChartDataInitialVariable, barChartOptionsInitialVariable } =
          initialGraphPrep(data)
        // console.log(data,"data....")
        updateGraphData(
          RecruiterPortal.Composite,
          barChartDataInitialVariable,
          barChartOptionsInitialVariable,
        )
      }
      if (
        (activeStates.activeTabName == RecruiterPortal.Composite ||
          activeStates.activeTabName == RecruiterPortal.Benchmark) &&
        activeStates.activeGraph == RecruiterPortal.Performance
      ) {
        const { performanceChartData, performaceChartOptions } =
          performanceChartDataPrep(data)
        // console.log(data,"data....")
        updateGraphData(
          RecruiterPortal.Performance,
          performanceChartData,
          performaceChartOptions,
        )
      }
      if (
        activeStates.activeTabName == RecruiterPortal.Benchmark &&
        activeStates.activeGraph == RecruiterPortal.Benchmark
      ) {
        const { barBenchmarkChartData, barBenchmarkChartOptions } =
          benchmarkPrep(data, chartState.bucketTiersData)
        updateGraphData(
          RecruiterPortal.Benchmark,
          barBenchmarkChartData,
          barBenchmarkChartOptions,
        )
      }
    }
  }
  return (
    <div>
      <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
        <Sidebar />
        <div
          className={isOpen ? "w-[80vw]" : "w-[100vw]"}
          style={{
            height: "100dvh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}>
          <React.Fragment>
            <Table
              columns={columnsStore.displayedColumns}
              data={[]}
              buttons={buttons}
              rowClickAction={() => {}}
              onSelectionChange={() => {}}
              showCheckboxColumn={false}
              onVisibleRowsChange={() => {}}
              filterConfig={filterConfig}
              loading={isLoading}
              setSearchText={() => {}}
              clearSearch={false}
              addPadding={false}
              setClearSearch={() => {}}
              metaData={[]}
              newData={tableContentToBeDisplayed}
              graphData={graphData}
              activeGraph={activeStates.activeGraph}
              graphPlaceHolder={graphPlaceHolder}
              updateDataByGroups={updateDataByGroups}
              onCurrentRowsChange={exportUsers}
              breadcrumbsDisplay={breadcrumbsDisplay}
              handleSearchUpdateWithGraph={handleSearchUpdateWithGraph}
            />
          </React.Fragment>

          <ManageModels
            isOpen={modelAndButtonState.isModelOpen}
            setIsModelOpen={handleModelToggle}
            buttonSelection={modelAndButtonState.buttonSelection ?? ""}
            measuresMenu={measureData.measuresMenu}
            compositeScoresMenu={measureData.compositeScoresMenu}
            handleSubmit={handleSubmit}
            maxScore={chartState.minMaxScore.max}
          />
        </div>
      </div>
    </div>
  )
}

export default RecruiterDashboard
